import React, { useEffect, useLayoutEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { codeValidatorAction, resetEmailCheckerAction } from "@redux/actions";
import { Button, ButtonGroup, Form, Stack } from "react-bootstrap";
import { initialValues, validationSchema } from "./SignUpStepData";
import { APP_TITLE_SUFFIX, INSTRUCTOR_ROLE, STUDENT_ROLE } from "@constants";
import styles from "./styles/SignUpStep.module.css";
import { SelectField, TextArea } from "@components";

const SignUpStep = () => {
  // state

  // variables
  const roles = [INSTRUCTOR_ROLE, STUDENT_ROLE];
  let roleSwitcher = null;
  let greeting = "Sign Up and Start Learning!";

  // logics before hooks
  const handleNext = (values) => {
    const { isInstructor, categoryId, ...rest } = values;
    dispatch(
      codeValidatorAction({
        data: [state.email, "Register"],
        onSuccess: () =>
          navigate("/code-validate", {
            state: isInstructor
              ? { isInstructor, ...state, categoryId, ...rest }
              : { isInstructor, ...state, ...rest },
          }),
      })
    );
  };

  // hooks
  const { allCategories } = useSelector((state) => state.category);
  const { emailChecker } = useSelector((state) => state.auth);
  const { state } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldError,
    setFieldTouched,
    resetForm,
    values,
    errors,
    touched,
  } = useFormik({
    initialValues: {
      isInstructor: state.isInstructor ?? initialValues.isInstructor,
      categoryId: state.categoryId || initialValues.categoryId,
      description: state.description || initialValues.description,
    },
    validationSchema,
    onSubmit: handleNext,
  });

  // logics
  if (values.isInstructor) {
    greeting = "Sign Up and Create Course!";
  }

  const handleChangeRole = (value) => {
    resetForm({ values: { ...initialValues, isInstructor: value } });
  };

  const handleReturn = () => {
    navigate("/sign-up", { state: { ...state, ...values } });
  };

  // UI partials
  roleSwitcher = roles.map((value, index) => (
    <Button
      key={index}
      variant={
        value === (values.isInstructor ? INSTRUCTOR_ROLE : STUDENT_ROLE)
          ? "primary"
          : "light"
      }
      onClick={() => {
        handleChangeRole(value === INSTRUCTOR_ROLE);
      }}
    >
      <span>Sign Up as {value}</span>
    </Button>
  ));

  const signUpStepForm = (
    <Form noValidate onSubmit={handleSubmit}>
      <Stack gap={4}>
        {values.isInstructor ? (
          <Form.Group controlId="categoryId">
            <SelectField
              name={"categoryId"}
              value={values.categoryId || ""}
              onChange={(val) => setFieldValue("categoryId", val)}
              listValue={allCategories.data || []}
              isLoading={allCategories.processing}
              placeholder={"Select Category"}
              error={errors.categoryId}
              setFieldError={() =>
                setFieldError(
                  "categoryId",
                  "Category is required for instructor"
                )
              }
              touched={touched.categoryId}
              setFieldTouched={() => {
                setFieldTouched("categoryId", true);
              }}
              multiple={false}
            />
          </Form.Group>
        ) : null}
        <Form.Group controlId="description">
          <TextArea
            placeholder={"Write a little description about you..."}
            rows={4}
            value={values.description}
            onChange={handleChange}
            onBlur={handleBlur}
            autoComplete="off"
            touchedValue={touched.description}
            errorsValue={errors.description}
          />
        </Form.Group>
        <div>
          By signing up, you agree to our{" "}
          <Link to={"/term-of-use"}>Terms of Use</Link> and{" "}
          <Link to={"/privacy-policy"}>Privacy Policy</Link>.
        </div>
        <ButtonGroup>
          <Button variant="light" type="button" onClick={handleReturn}>
            <i className="bi bi-arrow-left mr-2" />
            Previous Step
          </Button>
          <Button type="submit">
            Next Step
            <i className="bi bi-arrow-right ml-2" />
          </Button>
        </ButtonGroup>
      </Stack>
    </Form>
  );

  // effects
  useEffect(() => {
    if (emailChecker.data !== null) dispatch(resetEmailCheckerAction());
  }, [dispatch]);

  useLayoutEffect(() => {
    document.title = `Sign up - 2/3 | ${APP_TITLE_SUFFIX}`;
  }, []);

  // render
  if (!state) return <Navigate to={"/sign-up"} />;
  return (
    <Stack gap={4} className={styles.container}>
      <ButtonGroup>{roleSwitcher}</ButtonGroup>
      <span>{greeting}</span>
      {signUpStepForm}
      <div>
        Already have an account? <Link to={"/sign-in"}>Sign In</Link>
      </div>
    </Stack>
  );
};

/* required: display name */
SignUpStep.displayName = "SignUpStep";

/* required: props types */
SignUpStep.propTypes = {};

/* required: default props */
SignUpStep.defaultProps = {};

export { SignUpStep };
